import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap,
  Globe,
  TrendingUp,
  MessageSquare,
  ArrowRight,
  Check,
  Menu,
  X,
  ChevronRight,
  Sparkles,
  Cpu,
  BarChart3,
  Users,
  Clock,
  Shield,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import logo from "../assets/favicon.png";

const API = process.env.REACT_APP_API_URL;

if (!API) {
  console.error("REACT_APP_API_URL no está definida. Los requests de contacto van a fallar.");
}

// -------------------- HELPERS --------------------

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// -------------------- LOGO --------------------

const Logo = ({ showName = true, size = "default" }) => (
  <div className="flex items-center gap-3">
    <img
      src={logo}
      alt="Wol Dynamics"
      className={size === "large" ? "h-14 w-auto" : "h-10 w-auto"}
    />
    {showName && (
      <span
        className={`font-heading font-bold text-foreground leading-none ${
          size === "large" ? "text-2xl" : "text-xl"
        }`}
      >
        WOL <span className="text-primary">DYNAMICS</span>
      </span>
    )}
  </div>
);

// -------------------- WHATSAPP ICON --------------------

const WhatsAppIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// -------------------- NAVIGATION --------------------

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    // passive: true para no bloquear el hilo principal en móviles
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#servicios", label: "Servicios" },
    { href: "#planes", label: "Planes" },
    { href: "/soluciones", label: "¿Por qué nosotros?" },
    { href: "#sobre-mi", label: "Sobre Nosotros" },
    { href: "#contacto", label: "Contacto" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-strong py-3" : "py-5"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#" className="flex items-center gap-3" data-testid="logo-link">
          <Logo showName={true} />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors font-body"
              data-testid={`nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:block">
          <a href="#contacto">
            <Button
              className="skew-button bg-primary hover:bg-primary/90 text-black font-semibold px-6"
              data-testid="nav-cta-button"
            >
              <span className="skew-button-content flex items-center gap-2">
                Consulta Gratis
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </a>
        </div>

        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden glass-strong mt-3 mx-6 rounded-lg p-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-foreground hover:text-primary transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#contacto" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-primary hover:bg-primary/90 text-black font-semibold mt-2">
                Consulta Gratis
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// -------------------- HERO --------------------

const HeroSection = () => (
  <section
    className="relative min-h-screen flex items-center justify-center overflow-hidden hero-pattern"
    data-testid="hero-section"
  >
    <div className="absolute inset-0 grid-pattern opacity-50" />
    <div className="orb orb-1" />
    <div className="orb orb-2" />

    <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-16">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-8 animate-fade-in">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="text-sm font-mono text-muted-foreground">
            AUTOMATIZACIÓN · DESARROLLO · MARKETING
          </span>
        </div>

        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-8 animate-slide-up"
          style={{ animationDelay: "0.1s", lineHeight: "1.3" }}
        >
          Transformamos tu negocio
          <span className="block text-foreground mt-2">con</span>
          <span className="block text-primary text-glow mt-2">Automatización Inteligente</span>
        </h1>

        <p
          className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-12 font-body animate-slide-up"
          style={{ animationDelay: "0.2s" }}
        >
          Optimizamos procesos, creamos experiencias digitales únicas y potenciamos tu presencia
          online con soluciones tecnológicas de vanguardia.
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up"
          style={{ animationDelay: "0.3s" }}
        >
          <a href="#contacto">
            <Button
              size="lg"
              className="skew-button bg-primary hover:bg-primary/90 text-black font-semibold px-8 py-6 text-base"
              data-testid="hero-cta-primary"
            >
              <span className="skew-button-content flex items-center gap-2">
                Comenzar Ahora
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </a>
          <a href="#servicios">
            <Button
              size="lg"
              variant="outline"
              className="skew-button border-primary/50 text-primary hover:bg-primary/10 px-8 py-6 text-base"
              data-testid="hero-cta-secondary"
            >
              <span className="skew-button-content flex items-center gap-2">
                Ver Servicios
                <ChevronRight className="w-5 h-5" />
              </span>
            </Button>
          </a>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mt-16 animate-slide-up"
          style={{ animationDelay: "0.4s" }}
        >
          {[
            { icon: Zap, label: "Respuesta rápida" },
            { icon: Shield, label: "Compromiso total" },
            { icon: TrendingUp, label: "Precios accesibles" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-center gap-3 glass px-4 py-3 rounded-full"
            >
              <item.icon className="w-5 h-5 text-primary" />
              <span className="text-sm text-muted-foreground">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce z-20">
      <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
        <div className="w-1 h-2 bg-primary rounded-full animate-pulse" />
      </div>
    </div>
  </section>
);

// -------------------- SERVICES --------------------

const ServicesSection = () => {
  const services = [
    {
      icon: Cpu,
      title: "Automatización de Procesos",
      description:
        "Eliminamos tareas repetitivas y optimizamos flujos de trabajo con sistemas inteligentes que trabajan 24/7.",
      features: ["Integración de sistemas", "Workflows automáticos", "Reportes en tiempo real"],
    },
    {
      icon: Globe,
      title: "Desarrollo Web",
      description:
        "Creamos sitios web y aplicaciones modernas, rápidas y optimizadas para convertir visitantes en clientes.",
      features: ["Diseño responsive", "SEO optimizado", "Alta velocidad"],
    },
    {
      icon: TrendingUp,
      title: "Marketing Digital",
      description:
        "Estrategias de marketing basadas en datos que maximizan tu ROI y escalan tu negocio.",
      features: ["Campañas publicitarias", "Email marketing", "Análisis de métricas"],
    },
  ];

  return (
    <section
      id="servicios"
      className="relative py-24 overflow-hidden"
      data-testid="services-section"
    >
      <div className="absolute inset-0 gradient-radial" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="text-primary font-mono text-sm tracking-wider">SERVICIOS</span>
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-8"
            style={{ lineHeight: "1.3" }}
          >
            Soluciones que impulsan tu <span className="text-primary">crecimiento</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Combinamos tecnología avanzada con estrategia de negocio para crear soluciones que
            generan resultados reales.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.title}
              className="service-card glass p-8 rounded-lg group hover:border-primary/50"
              data-testid={`service-card-${index}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <service.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-foreground mb-4">
                {service.title}
              </h3>
              <p className="text-muted-foreground text-base mb-6 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-3">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-base text-muted-foreground">
                    <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------- PRICING --------------------

const PricingSection = () => {
  const plans = [
    {
      name: "Plan Básico",
      price: "Cotización",
      description: "Automatización de un proceso",
      features: [
        "Análisis de procesos",
        "1 automatización personalizada",
        "Documentación completa",
        "30 días de soporte",
        "Capacitación básica",
      ],
      featured: false,
    },
    {
      name: "Plan Profesional",
      price: "Cotización",
      description: "Integración entre múltiples sistemas",
      features: [
        "Todo del Plan Básico",
        "Hasta 3 automatizaciones",
        "Integración de APIs",
        "Dashboard de métricas",
        "90 días de soporte",
        "Capacitación avanzada",
      ],
      featured: true,
    },
    {
      name: "Plan Empresa",
      price: "Cotización",
      description: "Automatización integral + soporte mensual",
      features: [
        "Todo del Plan Profesional",
        "Automatizaciones ilimitadas",
        "Arquitectura personalizada",
        "Soporte prioritario mensual",
        "Account manager dedicado",
        "SLA garantizado",
      ],
      featured: false,
    },
  ];

  return (
    <section
      id="planes"
      className="relative py-24 overflow-hidden"
      data-testid="pricing-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="text-primary font-mono text-sm tracking-wider">PLANES</span>
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-8"
            style={{ lineHeight: "1.3" }}
          >
            Inversión que genera <span className="text-primary">retorno</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Planes diseñados para cada etapa de tu negocio.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative rounded-lg p-8 ${
                plan.featured ? "price-card-featured glass-strong scale-105" : "glass"
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-black px-4 py-1 rounded-full text-xs font-bold">
                  MÁS POPULAR
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="font-heading text-2xl font-semibold text-foreground mb-3">
                  {plan.name}
                </h3>
                <p className="text-base text-muted-foreground mb-6">{plan.description}</p>
                <span
                  className={`font-heading text-5xl font-bold ${
                    plan.featured ? "text-glow-sm" : "text-foreground"
                  }`}
                >
                  {plan.price}
                </span>
              </div>

              <ul className="space-y-4 mb-10">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-base">
                    <Check
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        plan.featured ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <a href="#contacto" className="block">
                <Button
                  className={`w-full skew-button ${
                    plan.featured
                      ? "bg-primary hover:bg-primary/90 text-black"
                      : "bg-secondary hover:bg-secondary/80 text-foreground"
                  }`}
                  data-testid={`pricing-cta-${index}`}
                >
                  <span className="skew-button-content">Solicitar Cotización</span>
                </Button>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------- WHY CHOOSE US --------------------

const WhyChooseMeSection = () => {
  const reasons = [
    {
      icon: Zap,
      title: "Dedicación 100%",
      description:
        "Tu proyecto será nuestra prioridad. Sin distracciones, con atención personalizada de principio a fin.",
    },
    {
      icon: TrendingUp,
      title: "Precios justos",
      description:
        "Ofrecemos precios competitivos sin sacrificar calidad. Invertí en resultados, no en estructuras.",
    },
    {
      icon: Clock,
      title: "Comunicación directa",
      description:
        "Hablás directamente con el equipo, sin intermediarios. Respuestas rápidas y actualizaciones constantes.",
    },
    {
      icon: Sparkles,
      title: "Tecnología actual",
      description:
        "Usamos las herramientas y técnicas más modernas del mercado para crear soluciones eficientes.",
    },
  ];

  return (
    <section
      id="por-que-nosotros"
      className="relative py-24 overflow-hidden"
      data-testid="why-section"
    >
      <div className="absolute inset-0 gradient-radial" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <span className="text-primary font-mono text-sm tracking-wider">¿POR QUÉ NOSOTROS?</span>
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-8"
            style={{ lineHeight: "1.3" }}
          >
            Lo que nos hace <span className="text-primary">diferentes</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => (
            <div
              key={reason.title}
              className="glass p-8 rounded-lg group hover:border-primary/50 transition-colors"
              data-testid={`reason-card-${index}`}
            >
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <reason.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-3">
                {reason.title}
              </h3>
              <p className="text-muted-foreground text-base leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// -------------------- ABOUT --------------------

const AboutSection = () => (
  <section
    id="sobre-mi"
    className="relative py-24 overflow-hidden"
    data-testid="about-section"
  >
    <div className="absolute inset-0 gradient-radial" />

    <div className="relative z-10 max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-primary font-mono text-sm tracking-wider">SOBRE NOSOTROS</span>
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-8"
            style={{ lineHeight: "1.4" }}
          >
            Hola, soy el <span className="text-primary">fundador</span>
          </h2>
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              Tenemos una pasión enorme por la tecnología y la automatización. Decidimos crear{" "}
              <span className="text-primary font-semibold">Wol Dynamics</span> porque creemos que
              muchos negocios pueden mejorar sus procesos sin gastar fortunas.
            </p>
            <p>
              Somos autodidactas, curiosos y no paramos hasta lograr los resultados. Tenemos mucha
              dedicación y precios que se adaptan a tu realidad.
            </p>
            <p>
              Si estás buscando alguien que realmente se involucre en tu proyecto y que no te vea
              solo como un número más, no dudes en escribirnos.
            </p>
          </div>
        </div>

        <div className="glass-strong rounded-lg p-8 text-center">
          <div className="w-32 h-32 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-6">
            <span className="font-heading text-5xl font-bold text-primary">W</span>
          </div>
          <h3 className="font-heading text-2xl font-bold text-foreground mb-2">Wol Dynamics</h3>
          <p className="text-muted-foreground mb-6">Fundado en 2025</p>

          <div className="space-y-4">
            {[
              { icon: Users, label: "Atención personalizada" },
              { icon: Zap, label: "Respuesta en menos de 24hs" },
              { icon: Shield, label: "Compromiso real" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center justify-center gap-3 text-muted-foreground">
                <Icon className="w-5 h-5 text-primary" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// -------------------- CONTACT --------------------

const INITIAL_FORM = { name: "", email: "", phone: "", service: "", message: "" };

const ContactSection = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { name, email, service, message } = formData;

    if (!name.trim() || !email.trim() || !service || !message.trim()) {
      toast.error("Por favor completá todos los campos requeridos.");
      return;
    }
    if (!isValidEmail(email)) {
      toast.error("El email no tiene un formato válido.");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("El mensaje es demasiado corto.");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/contact`, formData);
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setFormData(INITIAL_FORM);
    } catch {
      toast.error("Error al enviar el mensaje. Intentá nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "bg-black/50 border-white/10 focus:border-primary/50 text-white h-14 text-base disabled:opacity-50";

  return (
    <section
      id="contacto"
      className="relative py-24 overflow-hidden"
      data-testid="contact-section"
    >
      <div className="absolute inset-0 gradient-radial" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Info */}
          <div>
            <span className="text-primary font-mono text-sm tracking-wider">CONTACTO</span>
            <h2
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-8"
              style={{ lineHeight: "1.3" }}
            >
              ¿Listo para <span className="text-primary">automatizar</span> tu negocio?
            </h2>
            <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
              Contanos sobre tu proyecto y te ayudamos a encontrar la mejor solución.
            </p>

            <div className="space-y-8">
              {[
                {
                  icon: Phone,
                  label: "WhatsApp",
                  content: (
                    <a
                      href="https://wa.me/5491135921999"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xl text-foreground hover:text-primary transition-colors font-medium"
                    >
                      +54 11 3592-1999
                    </a>
                  ),
                },
                {
                  icon: Clock,
                  label: "Horario",
                  content: (
                    <div className="text-xl text-foreground font-medium">Lun - Vie: 9:00 - 18:00</div>
                  ),
                },
                {
                  icon: Shield,
                  label: "Garantía",
                  content: (
                    <div className="text-xl text-foreground font-medium">
                      Satisfacción 100% garantizada
                    </div>
                  ),
                },
              ].map(({ icon: Icon, label, content }) => (
                <div key={label} className="flex items-center gap-5">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="text-base text-muted-foreground">{label}</div>
                    {content}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="glass-strong rounded-lg p-10">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-base text-muted-foreground mb-3">Nombre *</label>
                  <Input
                      type="text"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]/g, "");
                        if (val.length <= 50) setFormData({ ...formData, name: val });
                      }}
                      disabled={isSubmitting}
                      className={inputClass}
                      data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-base text-muted-foreground mb-3">Email *</label>
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={set("email")}
                    disabled={isSubmitting}
                    className={inputClass}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-base text-muted-foreground mb-3">Teléfono</label>
                  <Input
                      type="tel"
                      placeholder="+54 11 1234-5678"
                      value={formData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d+\-\s]/g, "");
                        if (val.length <= 15) setFormData({ ...formData, phone: val });
                      }}
                      disabled={isSubmitting}
                      className={inputClass}
                      data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-base text-muted-foreground mb-3">Servicio *</label>
                  <select
                    value={formData.service}
                    onChange={set("service")}
                    disabled={isSubmitting}
                    className="w-full bg-black/50 border border-white/10 focus:border-primary/50 text-white h-14 rounded-md px-4 appearance-none text-base disabled:opacity-50"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                      backgroundSize: "16px",
                    }}
                    data-testid="contact-service-select"
                  >
                    <option value="" disabled>
                      Seleccionar servicio
                    </option>
                    <option value="automatizacion">Automatización</option>
                    <option value="desarrollo">Desarrollo Web</option>
                    <option value="marketing">Marketing Digital</option>
                    <option value="consulta">Consulta General</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-base text-muted-foreground mb-3">Mensaje *</label>
                <Textarea
                  placeholder="Contanos sobre tu proyecto..."
                  value={formData.message}
                  onChange={set("message")}
                  disabled={isSubmitting}
                  className="bg-black/50 border-white/10 focus:border-primary/50 text-white min-h-[140px] resize-none text-base disabled:opacity-50"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full skew-button bg-primary hover:bg-primary/90 text-black font-semibold h-14 text-lg"
                data-testid="contact-submit-button"
              >
                <span className="skew-button-content flex items-center justify-center gap-2">
                  {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </span>
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// -------------------- FOOTER --------------------

const Footer = () => (
  <footer className="relative py-12 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <Logo showName={true} />
        <p className="text-sm text-muted-foreground text-center">
          © {new Date().getFullYear()} Wol Dynamics. Todos los derechos reservados.
        </p>
        <div className="flex items-center gap-6">
          {["#servicios", "#planes", "#contacto"].map((href) => (
            <a
              key={href}
              href={href}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors capitalize"
            >
              {href.replace("#", "")}
            </a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

// -------------------- WHATSAPP BUTTON --------------------

const WhatsAppButton = () => (
  <a
    href="https://wa.me/5491135921999"
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-button animate-pulse-glow"
    data-testid="whatsapp-button"
    aria-label="Contactar por WhatsApp"
  >
    <WhatsAppIcon />
  </a>
);

// -------------------- LANDING PAGE --------------------

const LandingPage = () => (
  <div className="min-h-screen bg-[#030305]">
    <Navigation />
    <HeroSection />
    <ServicesSection />
    <PricingSection />
    <WhyChooseMeSection />
    <AboutSection />
    <ContactSection />
    <Footer />
    <WhatsAppButton />
  </div>
);

export default LandingPage;